import { Check, ChevronDown, Search } from 'lucide-react';
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { cn } from './cn.js';
import { FloatingPanel } from './floating-panel.js';
import type { SelectOption } from './select.js';

/// Escolher várias opções sem que o filtro cresça junto com elas.
///
/// A alternativa era uma fileira de botões, um por opção. Funciona com quatro; com vinte telas ela
/// ocupa três linhas, empurra a lista para baixo e obriga a varrer tudo com o olho atrás de uma.
/// Aqui o filtro fechado ocupa uma linha e diz quantas estão marcadas; aberto, tem busca e rolagem,
/// então cem opções custam o mesmo espaço que quatro.
export function MultiSelect({
  label,
  options,
  selected,
  onChange,
  itemNoun,
  allLabel = 'Todas',
  noneLabel = 'Nenhuma',
  searchPlaceholder = 'Buscar',
  hideLabel = false,
  className,
  error,
  exclusiveValue,
  onSearchChange,
  loading = false,
  summary: summaryOverride,
}: {
  label: string;
  error?: string | undefined;
  exclusiveValue?: string | undefined;
  options: ReadonlyArray<SelectOption>;
  selected: readonly string[];
  onChange: (values: string[]) => void;
  /// Plural do que está sendo escolhido, para o resumo: "3 telas".
  itemNoun: string;
  allLabel?: string | undefined;
  noneLabel?: string | undefined;
  searchPlaceholder?: string | undefined;
  /// O rótulo fica só para o leitor de tela, quando a tela já diz o que é o campo.
  hideLabel?: boolean | undefined;
  className?: string | undefined;
  /// Busca no servidor, para cadastros sem teto natural: a lista mostra só o que a busca trouxe,
  /// sem filtrar de novo aqui, e some o "Marcar todas" — marcar o que não está à vista não faz
  /// sentido. Quem chama mantém as marcadas dentro de `options`.
  onSearchChange?: ((query: string) => void) | undefined;
  loading?: boolean | undefined;
  /// Nome de uma combinação conhecida, no lugar da contagem: A vencer + Vencidas é "Em aberto".
  summary?: string | undefined;
}) {
  const id = useId();
  const fieldRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const [query, setQuery] = useState('');

  const remote = onSearchChange !== undefined;
  const all = !remote && options.length > 0 && selected.length === options.length;
  const matches = useMemo(
    () =>
      remote || query.trim() === '' ? options : options.filter((o) => contains(o.label, query)),
    [options, query, remote],
  );

  /// O resumo diz o estado sem obrigar a abrir: tudo, nada, o nome quando é só uma, ou a conta.
  const summary =
    summaryOverride ??
    ((remote ? selected.length === 0 : options.length === 0)
      ? noneLabel
      : all
        ? allLabel
        : selected.length === 0
          ? noneLabel
          : selected.length === 1
            ? (options.find((option) => option.value === selected[0])?.label ?? '1 ' + itemNoun)
            : remote
              ? `${String(selected.length)} ${itemNoun}`
              : `${String(selected.length)} de ${String(options.length)} ${itemNoun}`);

  return (
    // Mesma altura, rótulo e espaçamento da caixa de escolha (`Combobox`): numa barra de filtros
    // os campos ficam lado a lado e precisam alinhar.
    <div className={cn('min-w-0 space-y-2', className)}>
      <span
        id={`${id}-label`}
        className={cn('block text-xs font-semibold text-content/70', hideLabel && 'sr-only')}
      >
        {label}
      </span>
      <div className="relative min-w-0" ref={fieldRef}>
        <button
          type="button"
          aria-label={`${label} ${summary}`}
          aria-controls={`${id}-options`}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
          onClick={() => setOpen((current) => !current)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setOpen(true);
            }
          }}
          className="hue-input flex h-12 w-full min-w-0 items-center gap-2 rounded-xl border border-surface-border bg-surface-elevated pl-4 pr-4 text-left text-content transition hover:border-brand-500/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
        >
          <span id={`${id}-value`} className="min-w-0 flex-1 truncate text-sm">
            {summary}
          </span>
          <ChevronDown
            aria-hidden="true"
            className={cn('size-4 shrink-0 text-content-muted transition', open && 'rotate-180')}
          />
        </button>

        {open ? (
          <FloatingPanel
            anchorRef={fieldRef}
            onDismiss={close}
            width="min-anchor"
            className="w-[min(20rem,85vw)] rounded-2xl border border-surface-border bg-surface-card p-2 shadow-card"
          >
            <div
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.stopPropagation();
                  setOpen(false);
                  fieldRef.current?.querySelector('button')?.focus();
                }
              }}
            >
              <div className="relative mb-2">
                <Search
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-content-faint"
                />
                <input
                  autoFocus
                  aria-label={`${searchPlaceholder} em ${label.toLowerCase()}`}
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    onSearchChange?.(event.target.value);
                  }}
                  placeholder={searchPlaceholder}
                  className="hue-input h-10 w-full rounded-lg border border-surface-border bg-surface-elevated pl-9 pr-3 text-sm text-content outline-none transition placeholder:text-content-faint focus:border-brand-500"
                />
              </div>

              <div
                className={cn(
                  'mb-1 flex items-center px-1',
                  remote ? 'justify-end' : 'justify-between',
                )}
              >
                {remote ? null : (
                  <button
                    type="button"
                    onClick={() =>
                      onChange(
                        options
                          .filter((option) => option.value !== exclusiveValue)
                          .map((option) => option.value),
                      )
                    }
                    className="rounded px-1 py-0.5 text-[11px] font-semibold text-brand-400 transition hover:text-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                  >
                    Marcar todas
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="rounded px-1 py-0.5 text-[11px] font-semibold text-content-muted transition hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                >
                  Nenhuma
                </button>
              </div>

              <ul
                id={`${id}-options`}
                role="listbox"
                aria-multiselectable="true"
                aria-label={label}
                className="max-h-64 overflow-y-auto"
              >
                {loading ? (
                  <li role="status" className="px-3 py-3 text-xs text-content-faint">
                    Buscando…
                  </li>
                ) : matches.length === 0 ? (
                  <li className="px-3 py-3 text-xs text-content-faint">Nada encontrado</li>
                ) : null}
                {matches.map((option) => {
                  const on = selected.includes(option.value);
                  return (
                    <li key={option.value}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={on}
                        onClick={() =>
                          onChange(
                            on
                              ? selected.filter((item) => item !== option.value)
                              : option.value === exclusiveValue
                                ? [option.value]
                                : [
                                    ...selected.filter((value) => value !== exclusiveValue),
                                    option.value,
                                  ],
                          )
                        }
                        className="flex min-h-10 w-full items-center gap-2.5 rounded-lg px-2 text-left transition hover:bg-surface-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                      >
                        <span
                          aria-hidden="true"
                          className={cn(
                            'grid size-4 shrink-0 place-items-center rounded border transition',
                            on
                              ? 'border-brand-500 bg-brand-500 text-on-brand'
                              : 'border-surface-border',
                          )}
                        >
                          {on ? <Check className="size-3" /> : null}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-xs font-medium">
                          {option.label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </FloatingPanel>
        ) : null}
      </div>
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-xs text-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/// Comparação sem acento e sem caixa: quem digita "lancamento" tem de achar "Lançamento".
function contains(haystack: string, needle: string): boolean {
  return fold(haystack).includes(fold(needle.trim()));
}

function fold(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}
